import './App.css';
import {
  Suspense,
  createContext,
  lazy,
  useEffect,
  useMemo,
  useState,
} from 'react';
import LandingPage from './Component/LandingPage/LandingPage';
import ConsentBanner from './Component/Common/ConsentBanner';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AUTH_STATE_EVENT, hasStoredAuthTokens, verifyLogin } from './Component/User/AuthUtils';
import { trackPageView } from './Utilities/AnalyticsUtils';

const LoginPage = lazy(() => import('./Component/User/Login'));
const SignupPage = lazy(() => import('./Component/User/SignUp'));
const CheckEmailPage = lazy(() => import('./Component/User/CheckEmail'));
const ConfirmEmailPage = lazy(() => import('./Component/User/ConfirmEmail'));
const ForgotPasswordPage = lazy(() => import('./Component/User/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./Component/User/ResetPassword'));
const Dashboard = lazy(() => import('./Component/Dashboard/Dashboard'));
const TermsPage = lazy(() =>
  import('./Component/Legal/LegalPages').then((module) => ({ default: module.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('./Component/Legal/LegalPages').then((module) => ({ default: module.PrivacyPage }))
);
const RefundPolicyPage = lazy(() =>
  import('./Component/Legal/LegalPages').then((module) => ({ default: module.RefundPolicyPage }))
);

export const UserContext = createContext(null);

const SITE_ORIGIN = 'https://webcredai.com';
const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}/social-preview.png`;
const TAILWIND_CDN_SRC = 'https://cdn.tailwindcss.com';

const DEFAULT_SEO = {
  title: 'WebCredAI | Fix SEO, UX & Conversion Issues Faster',
  description:
    'WebCredAI turns website audit noise into a prioritized action plan so you can fix the highest-impact SEO, UX, performance, and conversion issues first.',
  robots: 'index,follow',
  type: 'website',
};

function getSeoForPath(pathname = '/') {
  if (pathname === '/terms') {
    return {
      title: 'Terms of Service | WebCredAI',
      description:
        'Read the WebCredAI Terms of Service for account usage, credits, payments, and product access conditions.',
      robots: 'index,follow',
      type: 'article',
    };
  }

  if (pathname === '/privacy') {
    return {
      title: 'Privacy Policy | WebCredAI',
      description:
        'Read how WebCredAI handles account information, scan data, payment records, and support requests.',
      robots: 'index,follow',
      type: 'article',
    };
  }

  if (pathname === '/refund-policy') {
    return {
      title: 'Refund Policy | WebCredAI',
      description:
        'Review the WebCredAI refund and payment policy for credit purchases, payment disputes, and support resolution.',
      robots: 'index,follow',
      type: 'article',
    };
  }

  if (pathname === '/login') {
    return {
      title: 'Login | WebCredAI',
      description: 'Log in to WebCredAI to run page audits, review reports, and manage credits.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname === '/signup') {
    return {
      title: 'Create Your Account | WebCredAI',
      description: 'Create your WebCredAI account and get free credits to start auditing pages.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname === '/check-email') {
    return {
      title: 'Check Your Email | WebCredAI',
      description: 'Check your inbox to verify your WebCredAI account and continue setup.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname === '/confirm-email') {
    return {
      title: 'Confirm Email | WebCredAI',
      description: 'Confirm your email address to finish setting up your WebCredAI account.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname === '/forgot-password') {
    return {
      title: 'Forgot Password | WebCredAI',
      description: 'Reset your WebCredAI password to regain account access.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname === '/reset-password') {
    return {
      title: 'Reset Password | WebCredAI',
      description: 'Choose a new password for your WebCredAI account.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  if (pathname.startsWith('/dashboard')) {
    return {
      title: 'Dashboard | WebCredAI',
      description: 'Review audits, reports, projects, and credits inside your WebCredAI dashboard.',
      robots: 'noindex,nofollow',
      type: 'website',
    };
  }

  return DEFAULT_SEO;
}

function routeNeedsTailwind(pathname = '/') {
  return (
    pathname.startsWith('/dashboard') ||
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/check-email' ||
    pathname === '/confirm-email' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password'
  );
}

function upsertMetaTag({ name, property, content }) {
  if (typeof document === 'undefined') return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    if (name) tag.setAttribute('name', name);
    if (property) tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertLinkTag({ rel, href }) {
  if (typeof document === 'undefined') return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname || '/';
    const metadata = getSeoForPath(pathname);
    const canonicalUrl = `${SITE_ORIGIN}${pathname === '/' ? '/' : pathname}`;
    const pageLocation = `${SITE_ORIGIN}${pathname === '/' ? '/' : pathname}${location.search || ''}`;

    document.title = metadata.title;

    upsertMetaTag({ name: 'description', content: metadata.description });
    upsertMetaTag({ name: 'robots', content: metadata.robots });
    upsertMetaTag({ name: 'referrer', content: 'strict-origin-when-cross-origin' });
    upsertLinkTag({ rel: 'canonical', href: canonicalUrl });

    upsertMetaTag({ property: 'og:site_name', content: 'WebCredAI' });
    upsertMetaTag({ property: 'og:type', content: metadata.type });
    upsertMetaTag({ property: 'og:title', content: metadata.title });
    upsertMetaTag({ property: 'og:description', content: metadata.description });
    upsertMetaTag({ property: 'og:url', content: canonicalUrl });
    upsertMetaTag({ property: 'og:image', content: DEFAULT_SOCIAL_IMAGE });
    upsertMetaTag({ property: 'og:image:width', content: '1536' });
    upsertMetaTag({ property: 'og:image:height', content: '1024' });
    upsertMetaTag({ property: 'og:image:alt', content: 'WebCredAI landing page preview' });

    upsertMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMetaTag({ name: 'twitter:title', content: metadata.title });
    upsertMetaTag({ name: 'twitter:description', content: metadata.description });
    upsertMetaTag({ name: 'twitter:image', content: DEFAULT_SOCIAL_IMAGE });
    upsertMetaTag({ name: 'twitter:image:alt', content: 'WebCredAI landing page preview' });
    trackPageView({
      pagePath: `${pathname}${location.search || ''}`,
      pageLocation,
      pageTitle: metadata.title,
    });
  }, [location.pathname, location.search]);

  return null;
}

let tailwindScriptPromise = null;

function hasTailwindRuntime() {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector('script[data-tailwind-runtime="true"][data-loaded="true"]'));
}

function ensureTailwindRuntime() {
  if (typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (hasTailwindRuntime()) {
    return Promise.resolve(true);
  }
  if (!tailwindScriptPromise) {
    tailwindScriptPromise = new Promise((resolve) => {
      const existing = document.querySelector('script[data-tailwind-runtime="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = TAILWIND_CDN_SRC;
      script.async = true;
      script.dataset.tailwindRuntime = 'true';
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve(true);
      };
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }
  return tailwindScriptPromise;
}

function RoutePerformanceGate({ children }) {
  const location = useLocation();
  const needsTailwind = useMemo(
    () => routeNeedsTailwind(location.pathname || '/'),
    [location.pathname]
  );
  const [tailwindReady, setTailwindReady] = useState(() => !needsTailwind || hasTailwindRuntime());

  useEffect(() => {
    let active = true;
    if (!needsTailwind) {
      setTailwindReady(true);
      return () => {
        active = false;
      };
    }

    setTailwindReady(hasTailwindRuntime());
    ensureTailwindRuntime().then((ready) => {
      if (active) {
        setTailwindReady(Boolean(ready));
      }
    });

    return () => {
      active = false;
    };
  }, [needsTailwind]);

  if (needsTailwind && !tailwindReady) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: '#020617',
          color: '#E2E8F0',
          fontFamily: '"Avenir Next", "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderRadius: 18,
            border: '1px solid rgba(148, 163, 184, 0.18)',
            background: 'rgba(15, 23, 42, 0.82)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          Preparing WebCredAI...
        </div>
      </div>
    );
  }

  return children;
}

function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(() =>
    hasStoredAuthTokens() ? null : false
  );
  useEffect(() => {
    if (!hasStoredAuthTokens()) {
      setIsUserLoggedIn(false);
      return undefined;
    }

    const verifyUserLogin = async () => {
      setIsUserLoggedIn(await verifyLogin());
    };

    verifyUserLogin();

    const handleAuthStateChange = (event) => {
      const nextState = event?.detail?.isUserLoggedIn;
      if (typeof nextState === 'boolean') {
        setIsUserLoggedIn(nextState);
        return;
      }
      setIsUserLoggedIn(hasStoredAuthTokens());
    };

    window.addEventListener(AUTH_STATE_EVENT, handleAuthStateChange);
    return () => {
      window.removeEventListener(AUTH_STATE_EVENT, handleAuthStateChange);
    };
  }, []);
  return (
    <UserContext.Provider value={{ isUserLoggedIn, setIsUserLoggedIn }}>
      <BrowserRouter>
        <RouteMetadata />
        <RoutePerformanceGate>
          <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  isUserLoggedIn === null ? null : (
                    isUserLoggedIn ? <Navigate to="/dashboard/home" replace /> : <LandingPage />
                  )
                }
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/check-email" element={<CheckEmailPage />} />
              <Route path="/confirm-email" element={<ConfirmEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/dashboard/*"
                element={
                  isUserLoggedIn === null ? null : (
                    isUserLoggedIn ? <Dashboard /> : <Navigate to="/login" replace />
                  )
                }
              />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />

              {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
            </Routes>
          </Suspense>
        </RoutePerformanceGate>
        <ConsentBanner />
      </BrowserRouter>
    </UserContext.Provider>
  );
}

export default App;
